import Validator from 'validator'
import errorFromHttpResponse from '../utils/error-from-http-response'
import AnalyticsService from './AnalyticsService'
import LoggerService from './LoggerService'
import NetworkService from './NetworkService'
import ProductDiscoveryService from './ProductDiscoveryService'
import i18n from '@/i18n'

// Task 11 / STEP-10 and STEP-11: builds a mobile-registration body from a
// fixed allowlist of optional field names, dropping any that are absent or
// blank. Two reasons it is an allowlist rather than a spread: an unlisted
// field (phone, password, userId, challengeId) can never reach the wire, and
// a blank optional stays absent rather than becoming an empty string the
// backend would have to reject.
function withOptional(required, candidates, allowed) {
  const body = { ...required }
  for (const key of allowed) {
    const value = candidates[key]
    if (value === undefined || value === null || value === '') continue
    body[key] = value
  }
  return body
}

export default {
  login(creds) {
    const { email, password } = creds
    if (
      !email ||
      !password ||
      !Validator.isEmail(email) ||
      password.length < 1
    ) {
      return Promise.reject('Invalid login form submission')
    }

    return NetworkService.login(creds).then(res => {
      const data = { ...res.data }
      if (!data) {
        throw new Error('No user returned from auth service')
      }

      return data
    })
  },

  // Task 11 / STEP-08. Each wrapper sends exactly the body the accepted
  // backend contract names -- never a userId, a role, or (except sendMobile-
  // Challenge itself) a phone number -- and lets errorFromHttpResponse
  // preserve resp.status so the view can map 404 (rollout off) / 401
  // (invalid or expired) / 422 (invalid phone) / other into its own small
  // controlled error set without inspecting raw backend error bodies.
  sendMobileChallenge(phone) {
    return NetworkService.sendMobileChallenge({ phone })
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  confirmMobileVerification({ challengeId, code }) {
    return NetworkService.confirmMobileVerification({ challengeId, code })
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  resolveMobileIdentity(ownershipGrantId) {
    return NetworkService.resolveMobileIdentity({ ownershipGrantId })
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  establishMobileSession(ownershipGrantId) {
    return NetworkService.establishMobileSession({ ownershipGrantId })
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // Task 11 / STEP-10 and STEP-11: NEW-USER registration on a verified phone.
  // The payload is rebuilt explicitly from the named fields of the accepted
  // STEP-09 contract rather than spread from the caller, so a stray `phone`,
  // `password`, `userId` or `challengeId` on the form model can never reach
  // the wire. Optional fields that were left blank are omitted entirely
  // instead of being sent as empty strings, because the backend treats
  // "absent" and "empty" differently (email in particular is optional, and an
  // empty string is not a valid address).
  registerMobileStudent({
    registrationGrantId,
    firstName,
    lastName,
    terms,
    ...optional
  }) {
    return NetworkService.registerMobileStudent(
      withOptional(
        { registrationGrantId, firstName, lastName, terms },
        optional,
        [
          'email',
          'gradeLevel',
          'schoolId',
          'zipCode',
          'studentPartnerOrg',
          'studentPartnerSite',
          'referredByCode',
          // TASK-SF-06: the Student Foundation academic / geographic slugs
          // (02_* §5.1). Additive to this existing allowlist -- an unlisted
          // field still cannot reach the wire, and a blank one stays absent
          // rather than becoming an empty string the backend would reject.
          // Wire format is slugs, never ids.
          'educationStage',
          'educationGrade',
          'studyField',
          'province',
          'city',
          'schoolName',
        ]
      )
    )
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  // AUTH-G1 (02_* §15.9, §15.11): the unified "Continue with Google"
  // pre-login surface. `getGooglePending` reads only the safe subset the
  // backend exposes (email, firstName) -- never a sub, issuer or token.
  // `confirmGoogleRegistration`/`linkGoogleAccount` send only what the
  // accepted contract names; the Google identity itself is read from the
  // server-side session on the backend, never from this payload.
  getGooglePending() {
    return NetworkService.getGooglePending()
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  confirmGoogleRegistration({ terms, firstName, lastName }) {
    return NetworkService.confirmGoogleRegistration(
      withOptional({ terms }, { firstName, lastName }, [
        'firstName',
        'lastName',
      ])
    )
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  linkGoogleAccount(ownershipGrantId) {
    return NetworkService.linkGoogleAccount({ ownershipGrantId })
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  registerMobileVolunteer({
    registrationGrantId,
    firstName,
    lastName,
    terms,
    ...optional
  }) {
    return NetworkService.registerMobileVolunteer(
      withOptional(
        { registrationGrantId, firstName, lastName, terms },
        optional,
        [
          'email',
          'timezone',
          'volunteerPartnerOrg',
          'signupSourceId',
          'otherSignupSource',
        ]
      )
    )
      .then(res => ({ ...res.data }))
      .catch(err => {
        throw errorFromHttpResponse(err)
      })
  },

  registerOpenVolunteer(signupData) {
    return NetworkService.registerOpenVolunteer(signupData)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }
        AnalyticsService.registerVolunteer(data.user)
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  registerOpenVolunteerV2(signupData) {
    return NetworkService.registerOpenVolunteerV2(signupData)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }
        AnalyticsService.registerVolunteer(data.user)
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  registerPartnerVolunteer(signupData) {
    return NetworkService.registerPartnerVolunteer(signupData)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }
        AnalyticsService.registerVolunteer(data.user)
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  async registerStudent(signupData) {
    try {
      await NetworkService.registerStudent(signupData)
    } catch (e) {
      throw errorFromHttpResponse(e)
    }
  },

  async registerStudentV2(signupData) {
    try {
      await NetworkService.registerStudentV2(signupData)
    } catch (e) {
      throw errorFromHttpResponse(e)
    }
  },

  registerOpenStudent(signupData) {
    return NetworkService.registerOpenStudent(signupData)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  registerPartnerStudent(signupData) {
    return NetworkService.registerPartnerStudent(signupData)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  checkRegister(creds) {
    return NetworkService.checkRegister(creds).catch(res => {
      throw errorFromHttpResponse(res)
    })
  },

  sendReset(context, email, redirect) {
    return NetworkService.sendReset({ email })
      .then(res => {
        const data = { ...res.data }
        if (res.status !== 200) {
          throw new Error(data.err)
        }

        context.msg = data.msg

        if (redirect) {
          setTimeout(() => {
            context.$router.push(redirect)
          }, 2000)
        }
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  confirmReset(context, credentials, redirect) {
    return NetworkService.confirmReset(credentials)
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }

        context.msg = 'Password has been reset!'

        if (redirect) {
          setTimeout(() => {
            context.$router.push(redirect)
          }, 2000)
        }
      })
      .catch(res => {
        throw errorFromHttpResponse(res)
      })
  },

  initiateVerification(data) {
    return NetworkService.sendVerification(data).catch(err => {
      throw errorFromHttpResponse(err)
    })
  },

  initiateVerificationV2(data) {
    return NetworkService.sendVerificationV2(data).catch(err => {
      throw errorFromHttpResponse(err)
    })
  },

  confirmVerification(data) {
    return NetworkService.confirmVerification(data).catch(err => {
      throw errorFromHttpResponse(err)
    })
  },

  confirmVerificationV2(data) {
    return NetworkService.confirmVerificationV2(data).catch(err => {
      throw errorFromHttpResponse(err)
    })
  },

  // Borna bug batch 1 (item 4): the single logout implementation. The server
  // call, the store clear, the analytics/logger reset and the socket
  // disconnect are all unchanged -- only the destination moves, from the
  // legacy `LogoutView` ("you are logged out", with a link onward) to the
  // current Borna login experience. Both branches land in the same place, so a
  // failed server call cannot strand the user on a legacy page.
  logout(context) {
    if (context) {
      NetworkService.logout()
        .then(() => {
          context.$router.push(`/${i18n.locale}/login`)
          context.$store.dispatch('user/clear')
        })
        .catch(() => {
          context.$router.push(`/${i18n.locale}/login`)
          context.$store.dispatch('user/clear')
        })
        .finally(() => {
          AnalyticsService.reset()
          LoggerService.reset()
          ProductDiscoveryService.reset()

          // disconnect socket
          context.$socket.disconnect()
        })
    }
  },

  getAuth() {
    return NetworkService.user()
      .then(res => {
        const data = { ...res.data }
        if (!data) {
          throw new Error('No user returned from auth service')
        }

        if (data.user) {
          const auth = {
            authenticated: true,
            user: data.user,
          }
          // Task 11: the frontend and API are cross-origin in local dev
          // (:8080 -> :13000), and `Date` is not a CORS-safelisted response
          // header -- the API does not send `Access-Control-Expose-Headers`,
          // so `res.headers.get('Date')` is `undefined` in a real browser.
          // `new Date(undefined)` is an INVALID Date object -- and critically,
          // an Invalid Date is still *truthy*, so `updateUser`'s ordering
          // guard (`!user.date || ...`) could not detect it and treated it as
          // real ordering information forever after. Only store a Date here
          // when the header was actually present and parses to a real time;
          // never fabricate one (not `new Date()` either -- client receipt
          // time is not the same thing as server response ordering, and
          // pretending otherwise would just trade one wrong signal for
          // another). `state.user.date` unset is exactly the pre-existing,
          // already-handled "no ordering information yet" case.
          const dateHeader = res.headers && res.headers.get('Date')
          const parsedDate = dateHeader ? new Date(dateHeader) : null
          if (parsedDate && Number.isFinite(parsedDate.getTime())) {
            auth.user.date = parsedDate
          }
          // Task 11 / STEP-12 (Q-011): acting context travels alongside the
          // user rather than inside it, because it is a property of THIS
          // session, not of the identity. `roleSelectionRequired` is true only
          // for a dual-role user who has not chosen yet -- a single-role user
          // is resolved deterministically server-side and never sees it.
          auth.user.activeRole = data.activeRole ?? null
          auth.user.roleSelectionRequired = !!data.roleSelectionRequired
          return auth
        } else {
          return {
            authenticated: false,
            user: null,
          }
        }
      })
      .catch(err => {
        return {
          authenticated: false,
          user: null,
          err: err,
        }
      })
  },
}
